/**
 * Ticket WK5: die öffentliche Wahlkalender-Seite je Bundesland.
 *
 * Reihenfolge: Überschrift, erklärender Text, Weg zur Anmeldung, danach das
 * Gemeindeverzeichnis. Das Verzeichnis nennt nur Namen – keine Wahltermine,
 * keine amtierenden Personen, keine Unterseiten. Die Termine liegen bewusst
 * hinter der Anmeldung.
 *
 * Ticket WK7: Der Weg zur Anmeldung ist das Formular der Freebie-Strecke. Es
 * ist derselbe Baustein wie auf der Freebie-Seite (`LeadMagnetForm`), ohne
 * zweite Fassung; der bisherige Knopf entfällt. Ohne veröffentlichtes Freebie
 * trägt die Seite den bisherigen Knopf, damit der Weg zur Anmeldung nicht ins
 * Leere führt.
 *
 * Nach Freigabe 28.09.2026: Der Hero ist zweigeteilt — links Überschrift und
 * Einstiegstext, rechts die Stimmzettel-Animation der Bürgermeisterwahlseite
 * (derselbe Baustein, per lazySection im Browser nachgeladen). Das Formular
 * steht genau einmal, direkt unter dem Hero und vor den Textabschnitten.
 * Die Textabschnitte stehen als Aufklapp-Liste nach Kundenvorlage.
 *
 * Ist das Bundesland nicht freigeschaltet, antwortet die Seite mit 404, auch
 * wenn Gemeinden dazu im Bestand liegen – und damit gibt es auch kein Formular.
 */

import { createFileRoute, notFound } from "@tanstack/react-router";
import { NotFoundPage } from "@/components/NotFoundPage";
import { LandesseiteView } from "@/components/wahlkalender/LandesseiteView";
import { getLeadMagnetPage } from "@/lib/lead-magnets.functions";
import { loadWahlkalenderLandesseite } from "@/lib/wahlkalender-landesseite.functions";
import { WAHLKALENDER_MAGNET_SLUG, landesseiteHeadMeta, landesseitePath } from "@/lib/wahlkalender-landesseite";
import { siteUrl } from "@/lib/structured-data";

export const Route = createFileRoute("/wahlkalender/$bundesland")({
  loader: async ({ params }) => {
    const page = await loadWahlkalenderLandesseite({ data: { slug: params.bundesland } });
    if (!page) throw notFound();
    // Ticket WK7: dasselbe veröffentlichte Freebie wie auf der eigenen
    // Landingpage. Fehlt es, trägt die Seite den bisherigen Knopf.
    const magnet = await getLeadMagnetPage({ data: { slug: WAHLKALENDER_MAGNET_SLUG } });
    return { ...page, magnet };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Nicht gefunden" }, { name: "robots", content: "noindex" }] };
    }
    const url = siteUrl(landesseitePath(loaderData.content.bundesland));
    return {
      meta: landesseiteHeadMeta(loaderData.content, url),
      links: [{ rel: "canonical", href: url }],
    };
  },
  notFoundComponent: () => <NotFoundPage />,
  errorComponent: () => <NotFoundPage />,
  component: LandesseitePage,
});

function LandesseitePage() {
  return <LandesseiteView {...Route.useLoaderData()} />;
}
